// MSAL React provider. Initialises a singleton PublicClientApplication,
// handles the post-redirect promise, then renders <MsalProvider> which
// the rest of the app reads via useMsal(). Children never render until
// initialise() resolves so we don't briefly show an unauthenticated UI.
import {
  EventType,
  PublicClientApplication,
  type AuthenticationResult,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { useEffect, useState, type ReactNode } from "react";
import { msalConfig } from "./msalConfig";

const msalInstance = new PublicClientApplication(msalConfig);

// Set the active account whenever a successful login event fires so
// acquireTokenSilent can find an account without explicit lookups.
msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    event.payload &&
    "account" in event.payload &&
    event.payload.account
  ) {
    msalInstance.setActiveAccount(
      (event.payload as AuthenticationResult).account,
    );
  }
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await msalInstance.initialize();
      // Complete the redirect handshake (no-op if not returning from login).
      await msalInstance.handleRedirectPromise();
      // Restore the active account from cache (page refresh after sign-in).
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]!);
      }
      if (!cancelled) setReady(true);
    })().catch((err) => {
      console.error("[msal] initialize failed", err);
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return <div className="bsp-content-wide">Loading…</div>;
  }
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

export { msalInstance };
